/**
 * Features Section with animated feature cards
 */
import { useState } from 'react';
import { motion } from 'motion/react';
import {
  FileText,
  Upload,
  Database,
  ClipboardCheck,
  Users,
  Server,
  Lightbulb,
  FileSearch,
  Building2,
  Settings,
} from 'lucide-react';
import { content } from '@/landing/data/content';

const iconMap = {
  FileText,
  Upload,
  Database,
  ClipboardCheck,
  Users,
  Server,
  Lightbulb,
  FileSearch,
  Building2,
  Settings,
};

const personaFeatures = {
  'All': content.features,
  'Innovator': content.features.filter(f => 
    ['Innovation Submission & Registry', 'Document Uploads'].includes(f.name)
  ),
  'Reviewer': content.features.filter(f => 
    ['Structured Evaluation', 'Reviewer Assignment & Workload'].includes(f.name)
  ),
  'Institution': content.features.filter(f => 
    ['Bulk Import', 'Secure & Scalable Infrastructure'].includes(f.name)
  ),
};

export function Features() {
  const [selectedPersona, setSelectedPersona] = useState<keyof typeof personaFeatures>('All');

  return (
    <section id="features" className="relative py-24 lg:py-32 bg-[var(--navy)] border-t border-[rgba(201,168,76,0.15)]">
      <div className="absolute inset-0 hero-grid-overlay opacity-40" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[var(--gold)] mb-4">
            Powerful Features
          </h2>
          <p className="text-xl text-[var(--text-muted)] max-w-3xl mx-auto font-light">
            Everything you need to manage innovation from idea to IP
          </p>
        </motion.div>

        {/* Persona Toggles */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {Object.keys(personaFeatures).map((persona) => (
            <button
              key={persona}
              onClick={() => setSelectedPersona(persona as keyof typeof personaFeatures)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                selectedPersona === persona
                  ? 'bg-[var(--gold)] text-[var(--navy)] shadow-lg scale-105'
                  : 'bg-white/5 text-white/80 hover:bg-white/10 border border-[rgba(255,255,255,0.15)]'
              }`}
            >
              {persona}
            </button>
          ))}
        </motion.div>

        {/* Feature Grid */}
        <motion.div
          key={selectedPersona}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch"
        >
          {personaFeatures[selectedPersona].map((feature, index) => {
            const Icon = iconMap[feature.icon as keyof typeof iconMap];

            return (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -8 }}
                className="group relative insolip-card-gradient rounded-2xl p-8 border border-[rgba(255,255,255,0.08)] hover:border-[rgba(201,168,76,0.3)] transition-all duration-300 h-full flex flex-col"
              >
                {/* Icon and Category Tag — same row, gap on right of icon */}
                <div className="flex items-center gap-4 mb-4 flex-shrink-0">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className="flex-shrink-0 flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 border border-[rgba(201,168,76,0.3)]"
                  >
                    <Icon className="w-8 h-8 text-[var(--gold)]" />
                  </motion.div>
                  <div className="inline-block px-3 py-1 rounded-full bg-[var(--gold-pale)] text-[var(--gold)] text-xs font-semibold">
                    {feature.category}
                  </div>
                </div>

                {/* Content */}
                <h3 className="font-display text-xl font-semibold text-white mb-3 group-hover:text-[var(--gold-light)] transition-colors duration-300">
                  {feature.name}
                </h3>
                <p className="text-white/85 leading-relaxed font-light flex-1">
                  {feature.description}
                </p>

                </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-[var(--text-muted)] mb-4 font-light">
            Want to see all features in action?
          </p>
          <a
            href="#contact"
            className="inline-block px-8 py-4 insolip-cta-primary rounded-full hover:scale-105 transition-transform duration-300"
          >
            Request a Demo
          </a>
        </motion.div>
      </div>
    </section>
  );
}
